import type { ObjectGraph } from '@jet/environment/dependencies/object-graph';
import type { IntentController } from '@jet/environment/dispatching';
import { makePlaylistPaginationIntent } from './LibraryPlaylistPageIntent';
import { IntentKind } from '@jet-app/music-classical/intents';
import type {
    PaginationResult,
    PaginationIntent,
} from '@jet-app/music-classical/intents/common/pagination-invalidation-intent';
import { requestViewPageWithQuery } from '@jet-app/music-classical/api/request-view-page';
import type { PageChangeInstruction } from '../../../models/invalidation/change-instruction/PageChangeInstruction';
import { NetworkError } from '@jet-app/music-classical/utilities';
import type {
    ClassicalPaginatedResponse,
    ClassicalShelfItem,
} from '@jet-app/music-classical/api/types';
import { transformSections } from '@jet-app/music-classical/utilities/transforms/sections';
import { getFooterTrackMetadata } from '@jet-app/music-classical/utilities/transforms/playlist';
import { createClassicalAPITracklistSection } from './LibraryPlaylistPageIntentController';

export const LibraryPlaylistPaginationIntentController: IntentController<PaginationIntent> =
    {
        $intentKind: IntentKind.LIBRARY_PLAYLIST_PAGINATION,
        async perform(
            intent: PaginationIntent,
            objectGraph: ObjectGraph,
        ): Promise<PaginationResult> {
            const apiResponse = await requestViewPageWithQuery(
                intent.url,
                objectGraph,
            );

            const result = apiResponse.body as ClassicalPaginatedResponse;

            if (!result) {
                throw NetworkError.noContent();
            }

            const trackListSection = createClassicalAPITracklistSection(
                result.items as ClassicalShelfItem[],
            );

            const meta = getFooterTrackMetadata(result.items as any[]);

            const addItemsInstruction: PageChangeInstruction = {
                instruction: {
                    instruction: 'addSections',
                    sections: transformSections(
                        trackListSection,
                        objectGraph,
                        'playlist',
                    ),
                },
            };

            const updateFooterInstruction: PageChangeInstruction = {
                instruction: {
                    instruction: 'updateSection',
                    sectionID: 'track-list-footer',
                    meta,
                    objectGraph,
                },
            };

            const paginationResult: PaginationResult = {
                pageChangeInstructions: [
                    addItemsInstruction,
                    updateFooterInstruction,
                ],
            };

            if (result.next) {
                paginationResult.nextIntent = makePlaylistPaginationIntent({
                    id: intent.id,
                    url: result.next,
                } as Partial<PaginationIntent>) as PaginationIntent;
            }

            return Promise.resolve(paginationResult);
        },
    };
